import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SectionLabel from "./SectionLabel";

const BRANDS = [
  "CleanMaster Pro",
  "AI Optimizer",
  "SpeedBoost",
  "FileGuard",
  "BatteryLife+",
  "DataWizard",
  "AppShield",
  "StorageSense",
  "NetPulse",
  "PrivacyVault",
  "TaskFlow AI",
  "CloudSync",
  "SmartCleaner",
  "MemOptimize",
  "SecureWipe",
];

export default function InvestmentsSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const [hoveredBrand, setHoveredBrand] = useState<number | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000",
        padding: "60px 16px 100px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1440 }}>
        <div
          className="flex flex-col lg:grid gap-6 lg:gap-10"
          style={{ gridTemplateColumns: "repeat(12, minmax(0, 1fr))" }}
        >
          <div
            className="lg:col-span-3"
            style={{
              gridColumn: "span 3 / span 3",
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <SectionLabel>Our Products</SectionLabel>
          </div>
          <div
            className="lg:col-span-9"
            style={{
              gridColumn: "span 9 / span 9",
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(30px)",
              transition:
                "opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s",
            }}
          >
            <p
              style={{
                fontSize: "clamp(22px, 3vw, 44px)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: 0,
                fontWeight: 500,
              }}
            >
              <span style={{ color: "#ECECEC" }}>Our utility app portfolio</span>
              <span style={{ color: "#8A8A8A" }}>
                {" "}
                includes over 50 products across categories: cleaners, memory optimizers, AI services, data protection, and more. Each app is built with a focus on performance and user convenience.
              </span>
            </p>
            <div className="mt-6 lg:mt-8">
              <ExploreBtn />
            </div>
          </div>
        </div>
      </div>

      {/* Interactive marquee */}
      <div
        className="mt-16 lg:mt-24 relative"
        onMouseEnter={() => setMarqueePaused(true)}
        onMouseLeave={() => {
          setMarqueePaused(false);
          setHoveredBrand(null);
        }}
        style={{
          maskImage:
            "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
        }}
      >
        <div
          className="flex items-center"
          style={{
            width: "max-content",
            animation: "sg-marquee 40s linear infinite",
            animationPlayState: marqueePaused ? "paused" : "running",
          }}
        >
          {[...BRANDS, ...BRANDS].map((name, i) => {
            const isHover = hoveredBrand === i;
            const anyHover = hoveredBrand !== null;
            return (
              <div
                key={i}
                className="flex items-center whitespace-nowrap cursor-pointer"
                onMouseEnter={() => setHoveredBrand(i)}
                onMouseLeave={() => setHoveredBrand(null)}
                style={{
                  padding: "0 28px",
                  fontSize: "clamp(22px, 4vw, 38px)",
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  color: isHover ? "#93C5FD" : "#ECECEC",
                  opacity: anyHover && !isHover ? 0.3 : 0.9,
                  transform: isHover
                    ? "scale(1.08) translateY(-2px)"
                    : "scale(1)",
                  transition:
                    "color 0.35s ease, opacity 0.35s ease, transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
                }}
              >
                {name}
                <span
                  className="inline-block rounded-full ml-8 lg:ml-14"
                  style={{
                    width: isHover ? 14 : 10,
                    height: isHover ? 14 : 10,
                    background: isHover ? "#93C5FD" : "#3B82F6",
                    transition:
                      "width 0.35s ease, height 0.35s ease, background 0.35s ease",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Hover hint */}
        <div
          className="hidden lg:block"
          style={{
            position: "absolute",
            top: -32,
            right: 40,
            fontSize: 11,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "#8A8A8A",
            opacity: marqueePaused ? 1 : 0.55,
            transition: "opacity 0.3s ease",
          }}
        >
          {marqueePaused
            ? "◉ Paused — hover a brand"
            : "→ Hover to pause"}
        </div>
      </div>

      <style>{`
        @keyframes sg-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

function ExploreBtn() {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to="/our-products"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="whitespace-nowrap cursor-pointer relative overflow-hidden inline-flex items-center gap-3"
      style={{
        background: "transparent",
        color: hover ? "#000" : "#ECECEC",
        border: "1px solid rgba(255,255,255,0.55)",
        borderRadius: 9999,
        padding: "11px 22px",
        fontWeight: 600,
        fontSize: 14,
        transition: "color 0.35s ease, border-color 0.35s ease",
        borderColor: hover ? "#ECECEC" : "rgba(255,255,255,0.55)",
        textDecoration: "none",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 9999,
          background: "#ECECEC",
          transform: hover ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
      <span style={{ position: "relative" }}>View portfolio</span>
      <span
        aria-hidden
        style={{
          position: "relative",
          width: 18,
          height: 18,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transform: hover ? "translateX(4px)" : "translateX(0)",
          transition: "transform 0.35s ease",
        }}
      >
        <i className="ri-arrow-right-up-line" style={{ fontSize: 16 }} />
      </span>
    </Link>
  );
}